// LabelsModalContent.tsx
import React, { useEffect, useState } from "react"
import { Tabs } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTableList, faTag } from "@fortawesome/free-solid-svg-icons"
import { EditorRef } from "types/questionnaire.types"
import DataFileVariablesTab from "./DataFileVariablesTab.component"
import EditLabelsTab from "./EditLabelsTab.component"

/** Height taken up by the modal body padding, the tab bar, and its margin.
 * Kept slightly generous so each tab's footer never gets clipped. */
const CHROME_OFFSET = 188

type TabKey = "variables" | "labels"

/**
 * Props interface for the LabelsModalContent component
 */
interface LabelsModalContentProps {
  /** Ref to the editor component */
  editorRef: React.RefObject<EditorRef | null>
  /** Function to save the questionnaire */
  saveQuestionnaire: () => Promise<void>
  /** Function to set modal visibility */
  setIsModalVisible: (visible: boolean) => void
  /** Function to scroll to a specific line in the editor */
  scrollToLine?: (lineNumber: number) => void
  /** The height of the modal */
  modalHeight: number
  /** Optional label name to jump to when modal opens */
  jumpToLabel?: string | null
  /** Callback when jump is complete */
  onJumpComplete?: () => void
  /** Project identifier (for fetching the datamap) */
  projectId: string
  /** Project owner */
  owner: string
  /** Currently selected variant name ("" for master) */
  currentVariant: string
}

/**
 * LabelsModalContent hosts the two-tab Labels manager:
 *  - "Labels & Lists": the editable question labels, lists, range lists and
 *    exceptions (renames propagate across the editor). This is the default tab.
 *  - "Data File Variables": a read-only overview of what the authored labels
 *    become in the final data file (variable codes + value codes).
 *
 * @component
 */
const LabelsModalContent: React.FC<LabelsModalContentProps> = ({
  editorRef,
  saveQuestionnaire,
  setIsModalVisible,
  scrollToLine,
  modalHeight,
  jumpToLabel,
  onJumpComplete,
  projectId,
  currentVariant,
}) => {
  // Default to the editable Labels tab (also where a jump-to-label lands).
  const [activeKey, setActiveKey] = useState<TabKey>("labels")

  useEffect(() => {
    if (jumpToLabel) {
      setActiveKey("labels")
    }
  }, [jumpToLabel])

  const contentHeight = modalHeight - CHROME_OFFSET

  const items = [
    {
      key: "labels",
      label: (
        <span>
          <FontAwesomeIcon icon={faTag} className="mr-2" />
          Labels &amp; Lists
        </span>
      ),
      children: (
        <EditLabelsTab
          editorRef={editorRef}
          setIsModalVisible={setIsModalVisible}
          scrollToLine={scrollToLine}
          contentHeight={contentHeight}
          jumpToLabel={jumpToLabel}
          onJumpComplete={onJumpComplete}
        />
      ),
    },
    {
      key: "variables",
      label: (
        <span>
          <FontAwesomeIcon icon={faTableList} className="mr-2" />
          Data File Variables
        </span>
      ),
      children: (
        <DataFileVariablesTab
          projectId={projectId}
          currentVariant={currentVariant}
          isActive={activeKey === "variables"}
          contentHeight={contentHeight}
          saveQuestionnaire={saveQuestionnaire}
        />
      ),
    },
  ]

  return (
    <div
      className="bg-white"
      style={{ height: modalHeight - 120, overflow: "hidden" }}
    >
      <Tabs
        activeKey={activeKey}
        onChange={(key) => setActiveKey(key as TabKey)}
        items={items}
        className="px-2"
      />
    </div>
  )
}

export default LabelsModalContent
